import React from 'react';
import BemClass from '@upsales/components/Utils/bemClass';
import EventActivity from 'App/babel/components/EventActivity';
import { openEditOrder } from 'App/components/EditOrder';
import { useTranslation } from 'Components/Helpers/translate';
import AppService from 'App/legacy/AppService';
import useTimelineActivityOutcome from './useTimelineActivityOutcome';
import './TimelineActivityOutcome.scss';

type Props = {
	activityId: number;
	/** Collapses the row again, the way the Angular directive's `closeAndApply` did. */
	onClose: () => void;
};

/**
 * The expanded body of an activity row in the React timeline — the port of
 * `upEventActivity.tpl.html`'s `<tbody>`.
 *
 * With ACTIVITY_OUTCOMES the body is the shared
 * `App/babel/components/EventActivity`, which is the same component the Angular
 * card mounted through `react-driver`. Without the feature, Angular fell back
 * to a much smaller read-only block, which is what the second branch renders —
 * down to the plain `b`/`a`/`div` markup, because the row it sits in is styled
 * by `_timeline.scss` rather than by the component library.
 */
const TimelineActivityOutcome = ({ activityId, onClose }: Props) => {
	const classes = new BemClass('TimelineActivityOutcome');
	const { t } = useTranslation();
	const { eventDetails, loading, saving, isOutcomeEnabled, hasActivityOutcomeAccess, callbacks } =
		useTimelineActivityOutcome({ activityId, onClose });

	// Angular gated the whole `<tr>` on `!$$loading`, so an expanding row showed
	// nothing at all until the activity arrived rather than a spinner.
	if (loading || !eventDetails) {
		return null;
	}

	if (!hasActivityOutcomeAccess) {
		const { project, opportunity, notes, description } = eventDetails;

		return (
			<div className={classes.mod('plain').b()}>
				<b>{description}</b>
				{project || opportunity ? (
					<div>
						{`${t('activity.relatedTo')} `}
						{project ? (
							<>
								{`${t('default.theCampaign').toLowerCase()} `}
								<a
									onClick={event => {
										event.stopPropagation();
										Tools.$state.go('campaign.dashboard', {
											customerId: AppService.getCustomerId(),
											id: project.id
										});
									}}
								>
									{project.name}
								</a>
							</>
						) : null}
						{project && opportunity ? ` ${t('default.and').toLowerCase()} ` : null}
						{opportunity ? (
							<>
								{`${t('default.theOpportunity').toLowerCase()} `}
								<a
									onClick={event => {
										event.stopPropagation();
										openEditOrder({
											customerId: AppService.getCustomerId(),
											type: 'opportunity',
											id: opportunity.id
										});
									}}
								>
									{opportunity.description}
								</a>
							</>
						) : null}
					</div>
				) : null}
				{notes ? <div className={classes.elem('notes').b()}>{notes}</div> : null}
			</div>
		);
	}

	return (
		<div className={classes.b()}>
			<EventActivity
				eventDetails={eventDetails}
				company={eventDetails.client}
				isOutcomeEnabled={isOutcomeEnabled}
				disabled={saving}
				{...callbacks}
			/>
		</div>
	);
};

export default TimelineActivityOutcome;
